import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { preventDuplicateHero, validateHeroSections } from '@/utils/heroValidation';

export interface HomeSection {
  id: string;
  section_key: string;
  title_ar?: string;
  title_en?: string;
  subtitle_ar?: string;
  subtitle_en?: string;
  content_ar?: string;
  content_en?: string;
  button_text_ar?: string;
  button_text_en?: string;
  button_link?: string;
  background_url?: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useHomeSections = () => {
  return useQuery({
    queryKey: ['home-sections'],
    queryFn: async (): Promise<HomeSection[]> => {
  if (!isSupabaseConfigured) return [];
      const { data, error } = await supabase
        .from('home_sections')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useAdminHomeSections = () => {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['admin-home-sections'],
    queryFn: async (): Promise<HomeSection[]> => {
      if (!isAdmin) throw new Error('Unauthorized');
  if (!isSupabaseConfigured) return [];

      const { data, error } = await supabase
        .from('home_sections')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: isAdmin,
  });

  const createSection = useMutation({
    mutationFn: async (sectionData: Omit<HomeSection, 'id' | 'created_at' | 'updated_at'>) => {
      if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      if (!isAdmin) throw new Error('Unauthorized');

      // 🔒 Security: منع إنشاء Hero مكرر
      const existingSections = query.data || [];
      const heroCheck = preventDuplicateHero(existingSections, sectionData);
      
      if (!heroCheck.allowed) {
        throw new Error(heroCheck.reason);
      }

      const { data, error } = await supabase
        .from('home_sections')
        .insert([sectionData])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-home-sections'] });
      queryClient.invalidateQueries({ queryKey: ['home-sections'] });
      toast.success('تم إنشاء القسم بنجاح');
    },
    onError: () => {
      toast.error('حدث خطأ في إنشاء القسم');
    }
  });

  const updateSection = useMutation({
    mutationFn: async ({ id, ...sectionData }: Partial<HomeSection> & { id: string }) => {
  if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      if (!isAdmin) throw new Error('Unauthorized');

      const { data, error } = await supabase
        .from('home_sections')
        .update(sectionData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-home-sections'] });
      queryClient.invalidateQueries({ queryKey: ['home-sections'] });
      toast.success('تم تحديث القسم بنجاح');
    },
    onError: () => {
      toast.error('حدث خطأ في تحديث القسم');
    }
  });

  const deleteSection = useMutation({
    mutationFn: async (id: string) => {
  if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      if (!isAdmin) throw new Error('Unauthorized');

      const { error } = await supabase
        .from('home_sections')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-home-sections'] });
      queryClient.invalidateQueries({ queryKey: ['home-sections'] });
      toast.success('تم حذف القسم بنجاح');
    },
    onError: () => {
      toast.error('حدث خطأ في حذف القسم');
    }
  });

  const reorderSections = useMutation({
    mutationFn: async (sections: { id: string; sort_order: number }[]) => {
  if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      if (!isAdmin) throw new Error('Unauthorized');

      const updates = sections.map(section => 
        supabase
          .from('home_sections')
          .update({ sort_order: section.sort_order })
          .eq('id', section.id)
      );

      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-home-sections'] });
      queryClient.invalidateQueries({ queryKey: ['home-sections'] });
      toast.success('تم إعادة ترتيب الأقسام بنجاح');
    },
    onError: () => {
      toast.error('حدث خطأ في إعادة ترتيب الأقسام');
    }
  });

  const toggleSectionStatus = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
  if (!isSupabaseConfigured) throw new Error('Supabase not configured');
      if (!isAdmin) throw new Error('Unauthorized');

      const { data, error } = await supabase
        .from('home_sections')
        .update({ is_active })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-home-sections'] });
      queryClient.invalidateQueries({ queryKey: ['home-sections'] });
      toast.success(data.is_active ? 'تم تفعيل القسم' : 'تم إلغاء تفعيل القسم');
    },
    onError: () => {
      toast.error('حدث خطأ في تغيير حالة القسم');
    }
  });

  return {
    ...query,
    createSection,
    updateSection,
    deleteSection,
    reorderSections,
    toggleSectionStatus
  };
};